import { useEffect, useState } from 'react';

/**
 * Pieces shared by the corner event widgets — LaunchEventWidget and
 * TradingCompetitionPopup. Both are cut from the same Figma card (gradient +
 * noise plate, countdown, yellow CTA with the dot-matrix arrow). Everything
 * here is viewport-independent: the widgets own their responsive layout.
 */

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function getTimeLeft(target: Date): TimeLeft {
  const diff = Math.max(0, target.getTime() - Date.now());
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1000),
  };
}

export function pad(n: number) {
  return String(n).padStart(2, '0');
}

/**
 * Ticks once a second towards `target`. Null until mounted, so the server and
 * the first client render agree (the time left differs between the two).
 */
export function useTimeLeft(target: Date | null) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    if (!target) return;
    setTimeLeft(getTimeLeft(target));
    const interval = setInterval(() => setTimeLeft(getTimeLeft(target)), 1000);
    return () => clearInterval(interval);
  }, [target]);

  return timeLeft;
}

/**
 * The CTA's trailing glyph: a 7x7 dot grid with a chevron picked out in black,
 * exactly the cells Figma fills (the rest of the grid is transparent there).
 * Drawn from data rather than as an SVG so it stays the same shape as the
 * design system's other dot-matrix arrows.
 */
const ARROW_DOTS: Array<[col: number, row: number]> = [
  [1, 1], [1, 5],
  [2, 1], [2, 2], [2, 4], [2, 5],
  [3, 1], [3, 2], [3, 3], [3, 4], [3, 5],
  [4, 2], [4, 3], [4, 4],
  [5, 3],
];
const DOT_STEP_REM = 0.3846; // 3.0769px / 8
const DOT_SIZE_REM = 0.1923; // 1.538px / 8

export function ArrowDots() {
  return (
    <span className="relative block h-[2.5rem] w-[2.5rem] shrink-0" aria-hidden>
      {ARROW_DOTS.map(([col, row]) => (
        <span
          key={`${col}-${row}`}
          className="absolute rounded-[0.0435rem] bg-black"
          style={{
            left: `${col * DOT_STEP_REM}rem`,
            top: `${row * DOT_STEP_REM}rem`,
            width: `${DOT_SIZE_REM}rem`,
            height: `${DOT_SIZE_REM}rem`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * Background: the Figma gradient with the exported noise plate on top at 50%
 * exclusion — without it the card reads as flat green. Fills the nearest
 * positioned ancestor, which must clip its own overflow.
 */
export function EventCardBackground() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[0.5rem] bg-[linear-gradient(to_bottom,#10322a_24.539%,#319880_206.3%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[0.5rem] opacity-50 mix-blend-exclusion"
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative
            plate stretched to the card; next/image would add no value. */}
        <img
          src="/launch-event/noise.webp"
          alt=""
          className="absolute left-0 top-0 h-full w-full max-w-none object-cover"
        />
      </div>
    </>
  );
}
